import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Star, Quote } from "lucide-react";
import { BIZ, SLUG, PRACTICE_GROUPS, TEAM, REPUTATION } from "./kvpData";
import KvpLayout, { CHARCOAL, COPPER, CREAM, SLATE } from "./KvpLayout";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

export default function KvpHome() {
  const basePath = `/${SLUG}`;
  return (
    <KvpLayout active="home">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="kvp-hero">
        <img src="/law/kvp-cabahug-law-cebu-concept/atty-cabahug.jpg" alt="Atty. Karina Virginia Perpetua Cabahug" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(28,28,28,0.97) 0%, rgba(28,28,28,0.85) 45%, rgba(28,28,28,0.35) 100%)" }} />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 w-full">
          <motion.div {...fadeUp} className="max-w-xl">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: "#E39A6E" }}>Banilad, Cebu City</span>
            <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-white leading-[1.1]">Law, Property, Business &amp; Customs — One Cebu Firm</h1>
            <p className="mt-5 text-white/75 max-w-md">{BIZ.slogan}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={`${basePath}/contact`} data-testid="kvp-hero-cta" className="rounded-md px-7 py-3.5 text-sm font-semibold" style={{ background: COPPER, color: "white" }}>Request a Consultation</a>
              <a href={`${basePath}/practice-areas`} data-testid="kvp-hero-practice" className="inline-flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold text-white border border-white/30">Practice Areas <ArrowRight size={16} /></a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* REVIEW STRIP — lead with proof, since reviews are this firm's strongest evidence */}
      <section className="py-14" style={{ background: CREAM }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.p {...fadeUp} className="text-center text-xs uppercase tracking-[0.2em] font-semibold mb-8" style={{ color: COPPER }}>What Clients Say</motion.p>
          <div className="grid sm:grid-cols-2 gap-6">
            <motion.div {...fadeUp} className="bg-white rounded-lg p-6 shadow-sm">
              <Quote size={18} style={{ color: COPPER }} className="mb-2" />
              <p className="text-sm" style={{ color: CHARCOAL }}>"Fast verification of property legitimacy and documents, professionalism, transparency and efficiency."</p>
              <p className="mt-2 text-xs" style={{ color: SLATE }}>— Belinda, on property due diligence</p>
            </motion.div>
            <motion.div {...fadeUp} className="bg-white rounded-lg p-6 shadow-sm">
              <Quote size={18} style={{ color: COPPER }} className="mb-2" />
              <p className="text-sm" style={{ color: CHARCOAL }}>"Her expertise and support created confidence and ease, and how much she appeared to care."</p>
              <p className="mt-2 text-xs" style={{ color: SLATE }}>— Claude, on a contractor dispute</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRACTICE TEASER — horizontal list, not cards */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-extrabold text-center mb-10" style={{ color: CHARCOAL }}>What Sets This Firm Apart</motion.h2>
          <div className="divide-y" style={{ borderColor: "rgba(28,28,28,0.1)" }}>
            {PRACTICE_GROUPS.map((g) => (
              <motion.div key={g.key} {...fadeUp} className="py-4 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-bold text-sm" style={{ color: CHARCOAL }}>{g.title}</h3>
                <p className="text-xs" style={{ color: SLATE }}>{g.areas.join(" · ")}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM TEASER */}
      <section className="py-16" style={{ background: "white" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-extrabold text-center mb-10" style={{ color: CHARCOAL }}>Our Lawyers</motion.h2>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {TEAM.map((t) => (
              <motion.div key={t.name} {...fadeUp} className="text-center">
                <img src={t.photo} alt={t.name} className="w-full aspect-[3/4] object-cover object-top rounded-lg shadow-sm" />
                <p className="mt-2 text-xs font-semibold leading-tight" style={{ color: CHARCOAL }}>{t.name.split(",")[0]}</p>
                <p className="text-[11px]" style={{ color: SLATE }}>{t.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFICE — real photo, awards and certifications visible on the shelf */}
      <section>
        <img src="/law/kvp-cabahug-law-cebu-concept/office-meeting-room.jpg" alt="Inside the KVP Law office, Banilad, Cebu City" className="w-full h-64 sm:h-80 object-cover" />
      </section>

      {/* REPUTATION */}
      <section className="py-14" style={{ background: CHARCOAL }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <motion.div {...fadeUp} className="inline-flex items-center gap-1.5 mb-3">
            {[...Array(5)].map((_, i) => <Star key={i} size={18} fill={COPPER} style={{ color: COPPER }} />)}
          </motion.div>
          <motion.p {...fadeUp} className="text-2xl font-extrabold text-white">{REPUTATION.rating}★ · {REPUTATION.reviewCountLabel}</motion.p>
          <motion.p {...fadeUp} className="mt-2 text-xs text-white/60">The strongest recoverable review set in this concept batch — see reviews throughout the site, placed next to the services they describe.</motion.p>
        </div>
      </section>
    </KvpLayout>
  );
}
